import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, MapPin, Trophy, Users, Calendar } from "lucide-react";
import { api, teamOgImageUrl } from "@/lib/api";
import TeamLogo from "@/components/TeamLogo";
import PlayerCard from "@/components/PlayerCard";
import ScheduleTable from "@/components/ScheduleTable";
import PageMeta from "@/components/PageMeta";
import ShareButton from "@/components/ShareButton";

export default function TeamDetail() {
  const { slug } = useParams();
  const [team, setTeam] = useState(null);
  const [players, setPlayers] = useState([]);
  const [matches, setMatches] = useState([]);
  const [allTeams, setAllTeams] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setNotFound(false);
    Promise.all([
      api.get(`/teams/${slug}`),
      api.get(`/teams/${slug}/players`),
      api.get(`/teams/${slug}/matches`),
      api.get(`/teams`),
    ])
      .then(([t, p, m, all]) => {
        setTeam(t.data);
        setPlayers(p.data);
        setMatches(m.data);
        setAllTeams(all.data);
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="pshl-container py-32 text-center">
        <div className="font-display text-3xl text-white">Team not found.</div>
        <Link to="/teams" className="mt-6 inline-flex items-center gap-2 text-pshl-electric"><ArrowLeft className="h-4 w-4" /> Back to Teams</Link>
      </div>
    );
  }
  if (!team) {
    return <div className="pshl-container py-32 text-center text-white/60">Loading…</div>;
  }

  const teamsBySlug = Object.fromEntries(allTeams.map((t) => [t.slug, t]));
  const captain = players.find((p) => p.captain);
  const topRated = [...players].sort((a, b) => b.rating - a.rating).slice(0, 4);

  return (
    <div data-testid={`team-detail-${team.slug}`}>
      <PageMeta
        title={`${team.name} · ${team.city}`}
        description={`${team.name} — ${team.tagline} The official PSHL8 franchise representing ${team.city}, ${team.state}.`}
        image={teamOgImageUrl(team.slug)}
        imageAlt={`${team.name} — PSHL8 share card`}
        type="profile"
      />
      {/* Hero */}
      <section className="relative pt-20 md:pt-24 pb-6 overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{ background: `radial-gradient(800px 500px at 20% 30%, ${team.primary}40, transparent 65%), radial-gradient(700px 400px at 90% 80%, ${team.secondary}25, transparent 60%)` }}
        />
        <div className="absolute inset-0 speed-lines opacity-25" />
        <div className="relative pshl-container">
          <Link to="/teams" data-testid="back-to-teams" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-display text-white/60 hover:text-pshl-electric mb-8">
            <ArrowLeft className="h-3.5 w-3.5" /> All Teams
          </Link>

          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-3 flex justify-center lg:justify-start">
              <TeamLogo team={team} size={240} />
            </div>
            <div className="lg:col-span-9">
              <div className="font-mono text-xs tracking-[0.25em] text-pshl-electric uppercase">{team.abbr} · {team.state}</div>
              <h1 className="mt-2 font-display text-5xl md:text-6xl lg:text-7xl text-white leading-[0.92] tracking-tighter">{team.name}</h1>
              <p className="mt-4 text-base md:text-lg text-white/75 max-w-2xl">{team.tagline}</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <div className="inline-flex items-center gap-2 border border-white/15 px-4 py-2.5">
                  <MapPin className="h-3.5 w-3.5 text-pshl-electric" />
                  <span className="text-xs uppercase tracking-[0.18em] text-white/85">{team.city}</span>
                </div>
                <div className="inline-flex items-center gap-2 border border-white/15 px-4 py-2.5">
                  <Users className="h-3.5 w-3.5 text-pshl-electric" />
                  <span className="text-xs uppercase tracking-[0.18em] text-white/85">{players.length} Squad</span>
                </div>
                {captain && (
                  <div className="inline-flex items-center gap-2 border border-pshl-gold/40 bg-pshl-gold/10 px-4 py-2.5">
                    <Trophy className="h-3.5 w-3.5 text-pshl-gold" />
                    <span className="text-xs uppercase tracking-[0.18em] text-pshl-gold">Captain · {captain.name}</span>
                  </div>
                )}
                <ShareButton kind="team" slug={team.slug} title={`${team.name} · PSHL8`} label="Share Team" />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Top Rated */}
      {topRated.length > 0 && (
        <section className="section pt-8">
          <div className="pshl-container">
            <div className="text-xs uppercase tracking-[0.32em] text-pshl-electric font-display mb-3">Marquee Players</div>
            <h2 className="font-display text-3xl md:text-4xl text-white tracking-tighter mb-8">Top-Rated Squad.</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {topRated.map((p, i) => <PlayerCard key={p.id} player={p} team={team} index={i} />)}
            </div>
          </div>
        </section>
      )}

      {/* Full Squad */}
      <section className="section bg-[#08090E]">
        <div className="pshl-container">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="text-xs uppercase tracking-[0.32em] text-pshl-electric font-display mb-3">Squad</div>
              <h2 className="font-display text-3xl md:text-4xl text-white tracking-tighter">Full Roster.</h2>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {players.map((p, i) => <PlayerCard key={p.id} player={p} team={team} index={i} />)}
          </div>
        </div>
      </section>

      {/* Matches */}
      <section className="section">
        <div className="pshl-container">
          <div className="text-xs uppercase tracking-[0.32em] text-pshl-electric font-display mb-3">Matches</div>
          <h2 className="font-display text-3xl md:text-4xl text-white tracking-tighter mb-8 flex items-center gap-3">
            <Calendar className="h-7 w-7 text-pshl-electric" /> Season Fixtures
          </h2>
          {matches.length > 0 ? (
            <ScheduleTable schedule={matches} teamsBySlug={teamsBySlug} />
          ) : (
            <div className="text-white/60">No matches scheduled.</div>
          )}
        </div>
      </section>
    </div>
  );
}
